export const STICKER_PACKS = [
  { id: "harbour", title: "Harbour Kiwi", minLevel: 1, guest: true },
  { id: "anime", title: "Anime Kiwi", minLevel: 1, guest: false },
  { id: "sundrop", title: "Sundrop", minLevel: 1, guest: false },
  { id: "glowbean", title: "Glowbean", minLevel: 1, guest: false },
  { id: "luma", title: "Luma", minLevel: 1, guest: false },
  { id: "bao", title: "Bao", minLevel: 1, guest: false },
  { id: "baodream", title: "Bao Daydream", minLevel: 10, guest: false },
  { id: "stackie", title: "Stackie", minLevel: 10, guest: false },
  { id: "stackiecomic", title: "Stackie Comic", minLevel: 10, guest: false },
  { id: "stackieclay", title: "Stackie Clay", minLevel: 15, guest: false },
  { id: "mechbit", title: "Mechbit", minLevel: 15, guest: false },
  { id: "jellystar", title: "Jellystar", minLevel: 15, guest: false },
] as const;

export const STICKER_REACTIONS = [
  { id: "agree", label: "Good call", fallback: "👍" },
  { id: "care", label: "Care", fallback: "❤️" },
  { id: "laugh", label: "Laugh", fallback: "😂" },
  { id: "welcome", label: "Welcome", fallback: "👋" },
  { id: "kiwichat", label: "ManyHellos", fallback: "🥝" },
  { id: "idea", label: "Bright idea", fallback: "✨" },
  { id: "celebrate", label: "Celebrate", fallback: "🙌" },
  { id: "support", label: "Support", fallback: "💚" },
] as const;

export const MAX_STICKERS_PER_MESSAGE = 8;

export type StickerPackId = (typeof STICKER_PACKS)[number]["id"];
export type StickerReactionId = (typeof STICKER_REACTIONS)[number]["id"];
export type StickerToken = { packId: StickerPackId; reactionId: StickerReactionId };
export type StickerTextPart = { type: "text"; value: string } | ({ type: "sticker" } & StickerToken);

const stickerPackIds = new Set<string>(STICKER_PACKS.map((pack) => pack.id));
const stickerReactionIds = new Set<string>(STICKER_REACTIONS.map((reaction) => reaction.id));
const stickerTokenPattern = /\[\[mh:([a-z]+):([a-z]+)\]\]/g;

export function stickerPackById(id: string) {
  return STICKER_PACKS.find((pack) => pack.id === id);
}

export function stickerReactionById(id: string) {
  return STICKER_REACTIONS.find((reaction) => reaction.id === id);
}

export function unlockedStickerPacks(level: number, elevated: boolean, guest: boolean) {
  if (elevated) return [...STICKER_PACKS];
  return STICKER_PACKS.filter((pack) => guest ? pack.guest : level >= pack.minLevel);
}

export function stickerToken(packId: StickerPackId, reactionId: StickerReactionId) {
  return `[[mh:${packId}:${reactionId}]]`;
}

export function stickerTokens(value: string): StickerToken[] {
  const tokens: StickerToken[] = [];
  for (const match of value.matchAll(stickerTokenPattern)) {
    const packId = match[1] ?? "";
    const reactionId = match[2] ?? "";
    if (stickerPackIds.has(packId) && stickerReactionIds.has(reactionId)) {
      tokens.push({ packId: packId as StickerPackId, reactionId: reactionId as StickerReactionId });
    }
  }
  return tokens;
}

export function stickerTextParts(value: string): StickerTextPart[] {
  const parts: StickerTextPart[] = [];
  let cursor = 0;
  for (const match of value.matchAll(stickerTokenPattern)) {
    const index = match.index ?? 0;
    const packId = match[1] ?? "";
    const reactionId = match[2] ?? "";
    if (!stickerPackIds.has(packId) || !stickerReactionIds.has(reactionId)) continue;
    if (index > cursor) parts.push({ type: "text", value: value.slice(cursor, index) });
    parts.push({ type: "sticker", packId: packId as StickerPackId, reactionId: reactionId as StickerReactionId });
    cursor = index + match[0].length;
  }
  if (cursor < value.length) parts.push({ type: "text", value: value.slice(cursor) });
  return parts.length ? parts : [{ type: "text", value }];
}

export function stickerTextFallback(value: string) {
  return stickerTextParts(value).map((part) => {
    if (part.type === "text") return part.value;
    const pack = stickerPackById(part.packId);
    const reaction = stickerReactionById(part.reactionId);
    return `[${pack?.title ?? "ManyHellos"} · ${reaction?.label ?? "Sticker"}]`;
  }).join("");
}

export function isStickerOnlyMessage(value: string) {
  const parts = stickerTextParts(value);
  return parts.some((part) => part.type === "sticker")
    && parts.every((part) => part.type === "sticker" || !part.value.trim());
}
